import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { LuPlus, LuFileText, LuX } from "react-icons/lu";
import { toast } from "react-toastify";
import {
    getDesignMoodboards,
    uploadDesignMoodboards,
    deleteDesignMoodboard,
} from "../../../services/designBidService";

const MAX_FILES = 10;

const isPdf = (moodboard) =>
    String(moodboard?.file_extention || "")
        .toLowerCase()
        .includes("pdf");

// Moodboards section — same UI as the admin portal's design bid review
// Moodboards tab: dashed upload tile + square thumbnails with a
// hover-delete badge and an image lightbox.
export default function Moodboards({
    projectId,
    initialMoodboards = [],
    disabled = false,
}) {
    const inputRef = useRef(null);
    const [moodboards, setMoodboards] = useState(initialMoodboards);
    const [isUploading, setIsUploading] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [lightboxUrl, setLightboxUrl] = useState(null);

    useEffect(() => {
        setMoodboards(initialMoodboards || []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(initialMoodboards)]);

    const refresh = async () => {
        try {
            const images = await getDesignMoodboards(projectId);
            setMoodboards(images);
        } catch (error) {
            // Keep the current list if the refresh fails.
        }
    };

    const handleSelect = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (files.length === 0) return;
        if (files.length > MAX_FILES) {
            toast.error(`You can upload at most ${MAX_FILES} files at a time`);
            return;
        }
        setIsUploading(true);
        try {
            await uploadDesignMoodboards(projectId, files);
            toast.success("Moodboard(s) uploaded successfully");
            await refresh();
        } catch (error) {
            console.error("Failed to upload moodboards:", error);
            toast.error("Failed to upload moodboards");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (moodboard) => {
        setDeletingId(moodboard.id);
        try {
            await deleteDesignMoodboard(projectId, moodboard.id);
            toast.success("Moodboard deleted successfully");
            setMoodboards((prev) =>
                prev.filter((m) => m.id !== moodboard.id),
            );
        } catch (error) {
            console.error("Failed to delete moodboard:", error);
            toast.error("Failed to delete moodboard");
        } finally {
            setDeletingId(null);
        }
    };

    const openMoodboard = (moodboard) => {
        if (isPdf(moodboard)) {
            window.open(moodboard.url, "_blank", "noopener,noreferrer");
            return;
        }
        setLightboxUrl(moodboard.url);
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Box>
                <Typography
                    sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#0d0d0e",
                        fontFamily: "Inter Tight",
                        letterSpacing: 0,
                    }}
                >
                    Moodboards
                </Typography>
                <Typography
                    sx={{
                        fontSize: "12.5px",
                        color: "#949daa",
                        fontFamily: "Inter Tight",
                        letterSpacing: 0,
                        marginTop: "2px",
                    }}
                >
                    Upload reference images and PDFs for this design scope.
                </Typography>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    flexWrap: "wrap",
                }}
            >
                {!disabled && (
                    <>
                        <Box
                            component="button"
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            disabled={isUploading}
                            title="Upload images or PDFs"
                            sx={{
                                width: "96px",
                                height: "96px",
                                borderRadius: "8px",
                                border: "2px dashed #4D6BF6",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#4D6BF6",
                                backgroundColor: "transparent",
                                cursor: isUploading ? "default" : "pointer",
                                flexShrink: 0,
                                opacity: isUploading ? 0.6 : 1,
                                "&:hover": {
                                    backgroundColor: "#EFF6FF",
                                },
                            }}
                        >
                            {isUploading ? (
                                <CircularProgress size={18} />
                            ) : (
                                <>
                                    <LuPlus size={20} />
                                    <span
                                        style={{
                                            fontSize: "10px",
                                            fontWeight: 500,
                                            marginTop: "2px",
                                            fontFamily: "Inter Tight",
                                        }}
                                    >
                                        Upload
                                    </span>
                                </>
                            )}
                        </Box>
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*,application/pdf"
                            multiple
                            style={{ display: "none" }}
                            onChange={handleSelect}
                        />
                    </>
                )}

                {moodboards.map((moodboard) => (
                    <Box
                        key={moodboard.id}
                        sx={{
                            position: "relative",
                            flexShrink: 0,
                            "&:hover .moodboard-delete": {
                                display: "flex",
                            },
                        }}
                    >
                        <Box
                            onClick={() => openMoodboard(moodboard)}
                            sx={{
                                width: "200px",
                                height: "200px",
                                borderRadius: "8px",
                                border: "1px solid #E5E7EB",
                                backgroundColor: "#F9FAFB",
                                overflow: "hidden",
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {isPdf(moodboard) ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        color: "#EF4444",
                                        padding: "0 4px",
                                    }}
                                >
                                    <LuFileText size={22} />
                                    <span
                                        style={{
                                            fontSize: "9px",
                                            fontWeight: 500,
                                            color: "#949daa",
                                            marginTop: "4px",
                                            fontFamily: "Inter Tight",
                                        }}
                                    >
                                        PDF
                                    </span>
                                </Box>
                            ) : (
                                <img
                                    src={moodboard.url}
                                    alt="Moodboard"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                    }}
                                />
                            )}
                        </Box>
                        {!disabled && (
                            <Box
                                component="button"
                                type="button"
                                className="moodboard-delete"
                                onClick={() => handleDelete(moodboard)}
                                disabled={deletingId === moodboard.id}
                                title="Delete"
                                sx={{
                                    position: "absolute",
                                    top: "-6px",
                                    right: "-6px",
                                    width: "20px",
                                    height: "20px",
                                    borderRadius: "50%",
                                    backgroundColor: "#EF4444",
                                    color: "#fff",
                                    border: "none",
                                    display: "none",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    opacity:
                                        deletingId === moodboard.id ? 0.6 : 1,
                                }}
                            >
                                <LuX size={12} />
                            </Box>
                        )}
                    </Box>
                ))}
            </Box>

            {moodboards.length === 0 && disabled && (
                <Typography
                    sx={{
                        fontSize: "13px",
                        color: "#949daa",
                        fontFamily: "Inter Tight",
                    }}
                >
                    No moodboards uploaded yet.
                </Typography>
            )}

            {/* Lightbox */}
            {lightboxUrl && (
                <Box
                    onClick={() => setLightboxUrl(null)}
                    sx={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 1400,
                        backgroundColor: "rgba(0,0,0,0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "24px",
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        onClick={() => setLightboxUrl(null)}
                        sx={{
                            position: "absolute",
                            top: "16px",
                            right: "16px",
                            width: "36px",
                            height: "36px",
                            borderRadius: "50%",
                            border: "none",
                            backgroundColor: "rgba(255,255,255,0.15)",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                        }}
                    >
                        <LuX size={18} />
                    </Box>
                    <img
                        src={lightboxUrl}
                        alt="Moodboard"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            maxWidth: "90vw",
                            maxHeight: "85vh",
                            borderRadius: "8px",
                            objectFit: "contain",
                        }}
                    />
                </Box>
            )}
        </Box>
    );
}
